import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import {
  buildWorkPackageOwnerFilter,
  DND_IDENTIFIER,
  ErrorRetry,
  Message,
  PersonaMessages,
  PlanningActionType,
  PlanningWorkPackageSlider,
  SearchField,
  TextWithEllipsis,
  updateUnplanCache,
  useConfig,
  useCrewHierachy,
  useDateRange,
  useDebounce,
  usePlanning,
} from '@bechtel/client-common';
import { ASSIGN_CREW_MEMBER, GET_CREW_MEMBERS } from '@bechtel/client-gql';
import {
  Box,
  ButtonAppearance,
  ButtonSize,
  Heading,
  Icon,
  Radio,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldSize,
  theme,
  useModalState,
} from '@bechtel/gds';
import {
  AssignCrewMembersInput,
  AssignCrewMembersPayload,
  CrewMember,
  CrewMemberAssignment,
  CrewMemberList,
  CrewMemberShiftType,
  OwnershipType,
  PagingInput,
  PersonaType,
  SearchCrewMemberInput,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import { capitalize, forEach, get, map, reduce, sumBy, uniqBy } from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { CrewMemberShiftTypeMessages } from '~/components';
import { clearCacheByKey, QueryKeys } from '~/services/cache';
import AssignmentError from './assignment-error';
import { useWorkOwnerEditPermissions } from './hooks';
import { OwnerList } from './owner-list';
import {
  AssignButtonStyled,
  AssignSelectedUser,
  CrewMemberListStyled,
  FooterButtonStyled,
  Label,
  SelectItemStyled,
  SeparatorStyled,
  SideSheetSubHeaderStyled,
} from './styled';
import { AssignWorkOwnerProps, AssignWorkPackageInput } from './types';
import { UserSelectItem } from './user-select-item';

//TODO : Revisit to check if the shift type can be determined at back end
const getShiftTypes = (assignedCrewMembers: CrewMemberAssignment[]) => {
  const primaryShiftType: CrewMemberShiftType = assignedCrewMembers.some(
    (assignedCrewMember) => assignedCrewMember && assignedCrewMember.crewMember?.shiftType === CrewMemberShiftType.NIGHT
  )
    ? CrewMemberShiftType.NIGHT
    : CrewMemberShiftType.DAY;
  return [
    primaryShiftType,
    primaryShiftType === CrewMemberShiftType.NIGHT ? CrewMemberShiftType.DAY : CrewMemberShiftType.NIGHT,
  ];
};

const getShiftName = (crewMemberShiftType?: CrewMemberShiftType) => {
  return crewMemberShiftType ? (
    <>
      <Trans id={CrewMemberShiftTypeMessages[crewMemberShiftType].id} />
      &nbsp;{t`Shift`}
    </>
  ) : (
    ''
  );
};

const getAssignCrewMemberInput = (persona: PersonaType, ownerType: OwnershipType, crewMemberId?: CrewMember['id']) => {
  const assignedCrewMembersInput = [
    {
      persona: persona,
      crewMemberId: crewMemberId,
      ownershipType: ownerType,
    },
  ];
  if (persona === PersonaType.SUPERINTENDENT) {
    return [
      ...assignedCrewMembersInput,
      {
        persona: PersonaType.GENERAL_FOREMAN,
        crewMemberId: null,
        ownershipType: ownerType,
      },
      {
        persona: PersonaType.FOREMAN,
        crewMemberId: null,
        ownershipType: ownerType,
      },
    ];
  } else if (persona === PersonaType.GENERAL_FOREMAN) {
    return [
      ...assignedCrewMembersInput,
      {
        persona: PersonaType.FOREMAN,
        crewMemberId: null,
        ownershipType: ownerType,
      },
    ];
  }

  return assignedCrewMembersInput;
};

export const AssignWorkOwner: React.FC<AssignWorkOwnerProps> = ({
  onClose,
  assignedCrewMembers,
  secondaryOwners,
  otherOwners,
  workPackages,
  isPlanningModule = false,
}) => {
  const { numPlanningNextWeeks } = useConfig();
  const { i18n } = useLingui();
  const { tag: workPackageTag, startDate: startDate, endDate: endDate } = workPackages[0];
  const [editedPersona, setEditedPersona] = useState<PersonaType>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchText = useDebounce(searchQuery?.trim(), 500);
  const [crewUpdated, setCrewUpdated] = useState<boolean>(false);
  const [dateRangeLabel, dateDistance] = useDateRange(startDate, endDate);
  const [primaryShiftType, otherShiftType] = getShiftTypes(assignedCrewMembers);
  const { visible, open: openModal, close: closeModal } = useModalState(false);
  const [errorMsg, setErrorMsg] = useState<any>();
  const { planningDispatch } = usePlanning() || {};
  const [ownerType, setOwnerType] = useState<OwnershipType>(OwnershipType.PRIMARY);
  const [assignedCrewMemberIds, setAssignedCrewMemberIds] = useState<any>();
  const [isFESearch, setIsFESearch] = useState(false);
  const {
    selectedSuperintendent,
    selectedGeneralForeman,
    selectedForeman,
    planningWorkPackageSlider,
    totalSelectedCount,
    workPackageFilterInput,
  } = usePlanning();
  const isPrimaryOwnerAssigned: boolean = assignedCrewMembers?.some(
    (assignedCrewMember) => assignedCrewMember && assignedCrewMember.crewMember != null
  );

  // Use totalSelectedCount when "All" is selected, otherwise use actual list length
  const displayCount = useMemo(() => {
    if (isPlanningModule && planningWorkPackageSlider === PlanningWorkPackageSlider.All && totalSelectedCount) {
      return totalSelectedCount;
    }
    return workPackages?.length || 0;
  }, [isPlanningModule, planningWorkPackageSlider, totalSelectedCount, workPackages]);

  // Build filter owners similar to planning workpackage list
  const filterOwners = useMemo(() => {
    if (!isPlanningModule) return undefined;

    return buildWorkPackageOwnerFilter({
      selectedSuperintendent,
      selectedGeneralForeman,
      selectedForeman,
      filterByUnassignedPersona: [], // No drawer values in assign work owner
    });
  }, [isPlanningModule, selectedSuperintendent, selectedGeneralForeman, selectedForeman]);

  // Determine if "Apply to All" should be true
  const applyToAll = useMemo(() => {
    if (!isPlanningModule) return false;

    // applyToAll should be true ONLY when the "All" button is selected in the work package selection mode
    return planningWorkPackageSlider === PlanningWorkPackageSlider.All;
  }, [isPlanningModule, planningWorkPackageSlider]);
  const primaryOwnersEditPermissions = useWorkOwnerEditPermissions(
    assignedCrewMembers,
    secondaryOwners,
    otherOwners,
    OwnershipType.PRIMARY
  );
  const secondaryOwnersEditPermissions = useWorkOwnerEditPermissions(
    assignedCrewMembers,
    secondaryOwners,
    otherOwners,
    OwnershipType.SECONDARY
  );
  const OtherOwnersEditPermissions = useWorkOwnerEditPermissions(
    assignedCrewMembers,
    secondaryOwners,
    otherOwners,
    OwnershipType.OTHER
  );
  useEffect(() => {
    const assignedCrewMemberIds = {};

    forEach(assignedCrewMembers, (assignment) => {
      const persona = assignment?.persona;
      const crewMember = assignment?.crewMember;
      if (crewMember) {
        assignedCrewMemberIds[`${persona}-${OwnershipType.PRIMARY}`] = crewMember.id;
      }
    });

    forEach(secondaryOwners, (assignment) => {
      const persona = assignment.persona;
      const crewMember = assignment.crewMember;
      if (crewMember) {
        assignedCrewMemberIds[`${persona}-${OwnershipType.SECONDARY}`] = crewMember.id;
      }
    });
    forEach(otherOwners, (assignment) => {
      const persona = assignment.persona;
      const crewMember = assignment.crewMember;
      if (crewMember) {
        assignedCrewMemberIds[`${persona}-${OwnershipType.OTHER}`] = crewMember.id;
      }
    });

    setAssignedCrewMemberIds(assignedCrewMemberIds);
  }, [assignedCrewMembers, secondaryOwners, otherOwners]);
  const initialValues = {
    workPackageId: '',
    crewMemberId: '',
    shiftType: primaryShiftType,
    deletePlan: false,
  };

  const [searchCrewMembers, { data, loading: crewListLoading, error: crewListError, refetch, networkStatus }] =
    useCrewHierachy(true);

  const [
    searchFEMembers,
    {
      data: feData,
      loading: feListLoading,
      error: feListError,
      refetch: feListRefetch,
      networkStatus: feListnetworkStatus,
    },
  ] = useLazyQuery<{ crewMembers: CrewMemberList }, { input: SearchCrewMemberInput; paging: PagingInput }>(
    GET_CREW_MEMBERS,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    if (!editedPersona || editedPersona === PersonaType.FIELD_ENGINEER) return;
    const searchCrewInput = { searchKeyword: debouncedSearchText };
    if (editedPersona === PersonaType.GENERAL_FOREMAN) {
      searchCrewInput['superintendentId'] = assignedCrewMemberIds[`${PersonaType.SUPERINTENDENT}-${ownerType}`] || null;
    } else if (editedPersona === PersonaType.FOREMAN) {
      searchCrewInput['superintendentId'] = assignedCrewMemberIds[`${PersonaType.SUPERINTENDENT}-${ownerType}`] || null;
      searchCrewInput['generalForemanId'] =
        assignedCrewMemberIds[`${PersonaType.GENERAL_FOREMAN}-${ownerType}`] || null;
    }
    searchCrewMembers({
      variables: {
        input: searchCrewInput,
        paging: {
          cursor: '1',
          limit: 50,
        },
      },
    });
  }, [editedPersona, debouncedSearchText, ownerType]);

  useEffect(() => {
    if (!isFESearch) return;
    searchFEMembers({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
          persona: PersonaType.FIELD_ENGINEER,
        },
        paging: {
          cursor: '1',
          limit: 50,
        },
      },
    });
  }, [isFESearch, debouncedSearchText]);
  const [assignCrewMember, { loading: assignCrewMemberLoading }] = useMutation<
    { assignCrewMembers: AssignCrewMembersPayload },
    { input: AssignCrewMembersInput }
  >(ASSIGN_CREW_MEMBER);
  const crewMembers: CrewMemberList['items'] =
    editedPersona === PersonaType.FIELD_ENGINEER
      ? get(feData, 'crewMembers.items', [])
      : get(data, 'crewHierarchy.items', []);
  const setValuesOnClick = (persona: PersonaType) => {
    setSearchQuery('');
    setEditedPersona(persona);
  };

  const closeSideSheet = () => {
    if (crewUpdated) {
      if (isPlanningModule) {
        planningDispatch({
          type: PlanningActionType.ResetWorkPackageSelection,
          payload: { resetWorkPackagePager: true, selectedWpList: [] },
        });
      }
    }
    onClose();
  };
  const plannedHours = sumBy(workPackages, ({ plannedHours }) => (plannedHours ? plannedHours : 0));
  const refetchQueries = [`plannedMilestones`, `planningWeekStats`, `expiredMilestoneCount`, `earnableHours`];

  const getDisabledCrewMembersIDs = () => {
    const crewMembers = assignedCrewMembers.concat(secondaryOwners, otherOwners);
    return reduce(
      crewMembers,
      (result: string[], crewMember) => {
        const id = crewMember.crewMember?.id;
        if (id) result.push(id);
        return result;
      },
      []
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values: AssignWorkPackageInput, actions: FormikActions<AssignWorkPackageInput>) => {
        const { crewMemberId, persona, ownerType, shiftType, deletePlan, editedCrewMember } = values;
        if (!persona || !ownerType) return;

        // Build the mutation input
        const input: AssignCrewMembersInput = {
          workPackageIds: map(workPackages, (workpackage) => workpackage.id),
          assignedCrewMembers: getAssignCrewMemberInput(persona, ownerType, crewMemberId),
          deletePlan: deletePlan,
        };

        if (applyToAll) {
          // Use complete filter from planning context with all user selections if "All" is selected in work package selection, instead of just the filter from drawer, to ensure all user selections are honored
          input.workPackageFilterInput = workPackageFilterInput;
          input.applyToAll = applyToAll;
        }
        const { data: assignedCrewMembersData } = await assignCrewMember({
          variables: {
            input: input,
          },
          update: (cache, { data }) => {
            setCrewUpdated(true);
            if (
              data?.assignCrewMembers?.erroredWorkPackages?.length === 0 ||
              data?.assignCrewMembers?.erroredCrewMemberAssignments?.length === 0
            ) {
              setSearchQuery('');
            }
            if (
              persona === PersonaType.FOREMAN &&
              data?.assignCrewMembers.unplannedMilestones?.commodityMilestones?.length &&
              deletePlan
            ) {
              updateUnplanCache(
                cache,
                DND_IDENTIFIER.Unplanned,
                data?.assignCrewMembers.unplannedMilestones?.commodityMilestones,
                map(workPackages, (workpackage) => workpackage.id),
                editedCrewMember ? editedCrewMember?.id : '',
                numPlanningNextWeeks
              );
            }
            clearCacheByKey(cache, QueryKeys.EARNABLE_HOURS);
          },
          refetchQueries: isPlanningModule ? refetchQueries : [`workPackages`, ...refetchQueries],
        });
        actions.resetForm({ values: initialValues });
        actions.setFieldValue('ownerType', ownerType);
        actions.setFieldValue('shiftType', shiftType);
        setEditedPersona(undefined);
        actions.setSubmitting(false);
        if (
          get(assignedCrewMembersData, 'assignCrewMembers.erroredWorkPackages', [])?.length > 0 ||
          get(assignedCrewMembersData, 'assignCrewMembers.erroredCrewMemberAssignments', [])?.length > 0
        ) {
          openModal();
          setErrorMsg(assignedCrewMembersData);
        }
      }}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        submitForm,
        dirty,
        touched,
        resetForm,
      }: FormikProps<AssignWorkPackageInput>) => {
        return (
          <SideSheet
            size={editedPersona ? SideSheetSize.MEDIUM : SideSheetSize.DEFAULT}
            visible
            style={{
              boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
            }}
          >
            <SideSheetHeader title={t`Edit Ownership`}>
              <SideSheetClose
                onClick={closeSideSheet}
                style={{
                  backgroundColor: theme.colors.bechtel.blue[800],
                  boxShadow: 'none',
                }}
                disabled={assignCrewMemberLoading}
              ></SideSheetClose>
            </SideSheetHeader>
            <SideSheetBody data-testid="work-owners-list-sheet">
              <SideSheetContentCol
                style={{
                  boxShadow:
                    '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)',
                }}
              >
                <Box flexDirection={{ sm: 'column' }} p={{ sm: '500' }}>
                  <Box mb={{ sm: '200' }}>
                    {workPackages && displayCount > 1 ? (
                      <Heading size="500" weight="600" color="grey.1000">
                        {t`Multiple Work Packages`}
                        {`(${displayCount})`}
                      </Heading>
                    ) : (
                      <TextWithEllipsis size="500" color="grey.1000" weight="600">
                        {workPackageTag}
                      </TextWithEllipsis>
                    )}
                  </Box>
                  <Text size="400" color="grey.600">
                    {dateRangeLabel}
                    {dateDistance}
                  </Text>
                </Box>
                <SideSheetSubHeaderStyled>
                  <Box pt={{ sm: '300' }} justifyContent={{ sm: 'space-between' }}>
                    <Text size="400" weight="600" family="graphik" color="grey.1000">
                      <Trans>Primary Owners</Trans>
                    </Text>
                    <Icon icon="info-circle"></Icon>
                  </Box>
                </SideSheetSubHeaderStyled>
                <OwnerList
                  assignedCrewMembers={assignedCrewMembers}
                  onChange={(editedCrewMembers) => {
                    const uniquePersona = uniqBy(editedCrewMembers, 'crewMember.id');
                    const { crewMember, persona } = uniquePersona[0];
                    resetForm();
                    if (persona === PersonaType.FIELD_ENGINEER) {
                      setSearchQuery('');
                      setIsFESearch(true);
                      setEditedPersona(persona);
                    } else {
                      setIsFESearch(false);
                      setValuesOnClick(persona);
                    }
                    setOwnerType(OwnershipType.PRIMARY);
                    setFieldValue('editedCrewMember', crewMember);
                    setFieldValue('persona', persona);
                    setFieldValue('ownerType', OwnershipType.PRIMARY);
                    setFieldValue('isMultiOwnerEdit', uniquePersona.length > 1 ? true : false);
                  }}
                  {...primaryOwnersEditPermissions}
                ></OwnerList>
                {isPrimaryOwnerAssigned && (
                  <>
                    <SideSheetSubHeaderStyled>
                      <Box pt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
                        <Text size="400" weight="600" family="graphik" color="grey.1000">
                          {getShiftName(primaryShiftType)} <Trans>(Secondary)</Trans>
                        </Text>
                      </Box>
                    </SideSheetSubHeaderStyled>
                    <OwnerList
                      assignedCrewMembers={secondaryOwners}
                      onChange={(editedCrewMembers) => {
                        const uniquePersona = uniqBy(editedCrewMembers, 'crewMember.id');
                        const { crewMember, persona } = uniquePersona[0];
                        resetForm();
                        if (persona === PersonaType.FIELD_ENGINEER) {
                          setSearchQuery('');
                          setIsFESearch(true);
                          setEditedPersona(persona);
                        } else {
                          setIsFESearch(false);
                          setValuesOnClick(persona);
                        }
                        setOwnerType(OwnershipType.SECONDARY);
                        setFieldValue('editedCrewMember', crewMember);
                        setFieldValue('persona', persona);
                        setFieldValue('ownerType', OwnershipType.SECONDARY);
                        setFieldValue('shiftType', primaryShiftType);
                        setFieldValue('isMultiOwnerEdit', uniquePersona.length > 1 ? true : false);
                      }}
                      {...secondaryOwnersEditPermissions}
                    ></OwnerList>
                  </>
                )}
                {isPrimaryOwnerAssigned && (
                  <>
                    <SideSheetSubHeaderStyled>
                      <Box pt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
                        <Text size="400" weight="600" family="graphik" color="grey.1000">
                          {getShiftName(otherShiftType)} <Trans>(Secondary)</Trans>
                        </Text>
                      </Box>
                    </SideSheetSubHeaderStyled>
                    <OwnerList
                      assignedCrewMembers={otherOwners}
                      onChange={(editedCrewMembers) => {
                        const uniquePersona = uniqBy(editedCrewMembers, 'crewMember.id');
                        const { crewMember, persona } = uniquePersona[0];
                        resetForm();
                        if (persona === PersonaType.FIELD_ENGINEER) {
                          setSearchQuery('');
                          setIsFESearch(true);
                          setEditedPersona(persona);
                        } else {
                          setIsFESearch(false);
                          setValuesOnClick(persona);
                        }
                        setOwnerType(OwnershipType.OTHER);
                        setFieldValue('editedCrewMember', crewMember);
                        setFieldValue('persona', persona);
                        setFieldValue('ownerType', OwnershipType.OTHER);
                        setFieldValue('shiftType', otherShiftType);
                        setFieldValue('isMultiOwnerEdit', uniquePersona.length > 1 ? true : false);
                      }}
                      {...OtherOwnersEditPermissions}
                    ></OwnerList>
                  </>
                )}
              </SideSheetContentCol>
              {editedPersona && (
                <SideSheetContentCol data-testid="work-owners-edit-sheet">
                  {editedPersona === PersonaType.FOREMAN &&
                    plannedHours > 0 &&
                    values?.ownerType === OwnershipType.PRIMARY && (
                      <>
                        <SideSheetSubHeaderStyled>
                          <Box pt={{ sm: '400' }}>
                            <Text size="400" weight="600" family="graphik" color="grey.1000">
                              <Trans>Transfer Plan?</Trans>
                            </Text>
                          </Box>
                        </SideSheetSubHeaderStyled>
                        <Box px={{ sm: '500' }} pt={{ sm: '500' }} pb={{ sm: '300' }}>
                          <Text size="400" color="grey.1000" style={{ lineHeight: '1.57' }} family="graphik">
                            <Trans>
                              Work Packages contain milestones that have already been planned. Transfer plan to the new
                              assigned Foreman?
                            </Trans>
                          </Text>
                        </Box>
                        <Box justifyContent={{ sm: 'space-between' }} p={{ sm: '500' }} pb={{ sm: '600' }}>
                          <Box alignItems={{ sm: 'center' }}>
                            <Label
                              onClick={() => {
                                setFieldValue('deletePlan', false);
                              }}
                            >
                              <Box mr={{ sm: '300' }}>
                                <Radio readOnly checked={!values.deletePlan} />
                              </Box>
                              <Text size="400" color="grey.1000">
                                <Trans> Yes, transfer plan</Trans>
                              </Text>
                            </Label>
                          </Box>
                          <Box alignItems={{ sm: 'center' }}>
                            <Label
                              onClick={() => {
                                setFieldValue('deletePlan', true);
                              }}
                            >
                              <Box mr={{ sm: '300' }}>
                                <Radio readOnly checked={values.deletePlan} />
                              </Box>
                              <Text size="400" color="grey.1000">
                                <Trans> No, delete current plan</Trans>
                              </Text>
                            </Label>
                          </Box>
                        </Box>
                      </>
                    )}
                  <SideSheetSubHeaderStyled>
                    <Box pt={{ sm: '400' }}>
                      <Text size="400" weight="600" family="graphik" color="grey.1000">
                        {values?.editedCrewMember ? t`Edit` : t`Add`}&nbsp;
                        {i18n._(PersonaMessages[editedPersona])}
                        {isPrimaryOwnerAssigned ? ' - ' : ''}
                        {isPrimaryOwnerAssigned ? getShiftName(values?.shiftType) : ''}
                        {` (${capitalize(values?.ownerType)})`}
                      </Text>
                    </Box>
                  </SideSheetSubHeaderStyled>
                  <Box
                    my={{ sm: '400' }}
                    p={{ sm: '500' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                  >
                    <SearchField
                      size={TextFieldSize.LARGE}
                      placeholder={`${t`Search`} ${i18n._(PersonaMessages[editedPersona])}`}
                      type="search"
                      style={{
                        borderColor: `${theme.colors.grey[300]}`,
                        fontFamily: `${theme.fontFamilies['graphik']}`,
                        fontSize: `${theme.fontSizes[400]}`,
                      }}
                      onChange={(e: { target: { value: React.SetStateAction<string> } }) => {
                        setSearchQuery(e.target.value);
                      }}
                      value={searchQuery}
                      data-testid="search-user-text-box"
                    />
                  </Box>
                  <SideSheetSubHeaderStyled>
                    <Box pt={{ sm: '400' }}>
                      <Text size="400" weight="600" family="graphik" color="grey.1000">
                        {t`Select`}&nbsp;{i18n._(PersonaMessages[editedPersona])}
                      </Text>
                    </Box>
                  </SideSheetSubHeaderStyled>
                  {(crewListLoading ||
                    feListLoading ||
                    networkStatus === NetworkStatus.refetch ||
                    feListnetworkStatus === NetworkStatus.refetch) && (
                    <CrewMemberListStyled
                      alignItems={{ sm: 'center' }}
                      justifyContent={{ sm: 'center' }}
                      p={{ sm: '500' }}
                      data-testid="loading"
                    >
                      <Spinner size={SpinnerSize.LARGE} />
                    </CrewMemberListStyled>
                  )}
                  {!crewListLoading && crewListError && (
                    <ErrorRetry
                      refetch={() =>
                        refetch({
                          input: {
                            searchKeyword: searchQuery,
                            superintendentId:
                              assignedCrewMemberIds[`${PersonaType.SUPERINTENDENT}-${ownerType}`] || null,
                            generalForemanId:
                              assignedCrewMemberIds[`${PersonaType.GENERAL_FOREMAN}-${ownerType}`] || null,
                          },
                          paging: {
                            cursor: '1',
                            limit: 50,
                          },
                        })
                      }
                    >
                      <Trans>
                        There was a problem loading Crew Member List. Please click the Retry button below to try again.
                      </Trans>
                    </ErrorRetry>
                  )}

                  {!feListLoading && feListError && (
                    <ErrorRetry
                      refetch={() =>
                        feListRefetch({
                          input: {
                            searchKeyword: searchQuery,
                            persona: PersonaType.FIELD_ENGINEER,
                          },
                          paging: {
                            cursor: '1',
                            limit: 50,
                          },
                        })
                      }
                    >
                      <Trans>
                        There was a problem loading Crew Member List. Please click the Retry button below to try again.
                      </Trans>
                    </ErrorRetry>
                  )}

                  {!crewListLoading && !feListLoading && (
                    <CrewMemberListStyled flexDirection={{ sm: 'column' }}>
                      {(values?.editedCrewMember || values?.isMultiOwnerEdit) && (
                        <>
                          <SelectItemStyled
                            onClick={() => {
                              setFieldValue('crewMemberId', null);
                              setFieldTouched('crewMemberId', true);
                            }}
                            data-testid="unassign-item"
                            canAdd={true}
                          >
                            <Box mr={{ sm: '500' }} py={{ sm: '300' }} pl={{ sm: '300' }}>
                              <Radio checked={touched.crewMemberId && !values.crewMemberId ? true : false} readOnly />
                            </Box>
                            <UserSelectItem
                              imageUrl=""
                              id="Unassign"
                              firstName={t`Unassign`}
                              lastName=""
                            ></UserSelectItem>
                          </SelectItemStyled>
                          <SeparatorStyled></SeparatorStyled>
                        </>
                      )}
                      {crewMembers &&
                        crewMembers.map((crewMember) => {
                          return (
                            crewMember && (
                              <Fragment key={crewMember.id}>
                                <SelectItemStyled
                                  canAdd={
                                    !values.isMultiOwnerEdit &&
                                    (values?.editedCrewMember?.id === crewMember?.id ||
                                      getDisabledCrewMembersIDs().includes(crewMember?.id))
                                      ? false
                                      : true
                                  }
                                  onClick={() => {
                                    if (
                                      !values.isMultiOwnerEdit &&
                                      (values?.editedCrewMember?.id === crewMember?.id ||
                                        getDisabledCrewMembersIDs().includes(crewMember?.id))
                                    )
                                      return;

                                    setFieldValue('crewMemberId', crewMember?.id);
                                    setFieldTouched('crewMemberId', true);
                                    setFieldValue('persona', crewMember?.persona);
                                  }}
                                  data-testid="user-list-item"
                                >
                                  <Box mr={{ sm: '500' }} pl={{ sm: '300' }}>
                                    <Radio checked={values.crewMemberId === crewMember?.id} readOnly />
                                  </Box>
                                  <UserSelectItem key={crewMember.id} {...crewMember}></UserSelectItem>
                                </SelectItemStyled>
                                <SeparatorStyled></SeparatorStyled>
                              </Fragment>
                            )
                          );
                        })}
                      {crewMembers && crewMembers.length === 0 && <Message>No users found...</Message>}
                    </CrewMemberListStyled>
                  )}
                  <AssignSelectedUser>
                    <AssignButtonStyled
                      data-testid="update-ownership"
                      appearance={ButtonAppearance.SECONDARY}
                      size={ButtonSize.LARGE}
                      onClick={submitForm}
                      disabled={!touched.crewMemberId || !dirty || assignCrewMemberLoading}
                    >
                      {assignCrewMemberLoading && <Spinner></Spinner>}
                      {!assignCrewMemberLoading && <Trans>Assign Selected User</Trans>}
                    </AssignButtonStyled>
                  </AssignSelectedUser>
                </SideSheetContentCol>
              )}
            </SideSheetBody>
            <SideSheetFooter>
              <Box justifyContent={{ sm: 'flex-end' }}>
                <Box mr={{ sm: '400' }}>
                  <FooterButtonStyled
                    appearance={ButtonAppearance.SECONDARY}
                    onClick={closeSideSheet}
                    disabled={assignCrewMemberLoading}
                  >
                    <Trans>Cancel</Trans>
                  </FooterButtonStyled>
                </Box>
                <Box>
                  <FooterButtonStyled
                    data-testid="done-btn"
                    appearance={ButtonAppearance.SECONDARY}
                    onClick={closeSideSheet}
                    disabled={!crewUpdated}
                  >
                    <Trans>Done</Trans>
                  </FooterButtonStyled>
                </Box>
              </Box>
            </SideSheetFooter>
            {visible && <AssignmentError close={closeModal} error={errorMsg} />}
          </SideSheet>
        );
      }}
    </Formik>
  );
};
